import React, { useState } from 'react';
import { CheckIcon, PencilIcon, PlusIcon, SparklesIcon, TruckIcon, UtensilsIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, studioNav } from '../../../components/layout/SubNav';
import { Card, KeyValue, SectionHeading } from '../../../components/ui/Panels';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Field, Input, Select, Switch } from '../../../components/ui/Field';
import { Sheet } from '../../../components/ui/Sheet';
import { useSession } from '../../../contexts/SessionContext';
import { useLocale } from '../../../contexts/LocaleContext';
import { cuisineLabel, equipmentLabel } from '../../../data/taxonomy';
import { GROCERY_LABEL, SERVICE_FEE_RATE } from '../../../utils/pricing';
import { currency } from '../../../utils/format';
import type { ChefMenu, GroceryPolicy } from '../../../types/chef';
export function Menus() {
  const {
    chef
  } = useSession();
  const {
    locale,
    currencyCode
  } = useLocale();
  const [menus, setMenus] = useState<ChefMenu[]>(chef.menus);
  const [editing, setEditing] = useState<ChefMenu | null>(null);
  const [minimumSpend, setMinimumSpend] = useState(chef.minimumSpend);
  const [minDelivery, setMinDelivery] = useState(chef.minDeliveryOrder);
  const [deposit, setDeposit] = useState(chef.depositPercent);
  const [travelIncluded, setTravelIncluded] = useState(chef.travelIncludedKm);
  const [travelPerKm, setTravelPerKm] = useState(chef.travelPerKm);
  const [instant, setInstant] = useState(chef.bookingMode === 'instant');
  const [saved, setSaved] = useState(false);
  const cheapest = Math.min(...menus.map(m => m.pricePerGuest));
  const save = (next: ChefMenu) => {
    setMenus(menus.map(m => m.id === next.id ? next : m));
    setEditing(null);
  };
  const flash = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2400);
  };
  return <AppShell title="Menus & rates" subtitle="What guests compare you on" data-id="element-1357" data-mp="32xqdi1t10a98">
      <SubNav items={studioNav} data-id="element-1358" data-mp="32vng2wpjneuh" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1359" data-mp="2xz5eu6tf9ymw">
        <div className="space-y-8" data-id="element-1360" data-mp="2xwswvtnnzo9u">
          <section aria-labelledby="menu-list" data-id="element-1361" data-mp="3cnt8g7jpjya7">
            <SectionHeading level={2} title="Your menus" hint="Search shows the cheapest per-guest price. Add-ons stay separate so nothing looks hidden." action={<Button type="button" variant="outline" size="sm" data-id="element-1363" data-mp="2f018j5t9miyx">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1364" data-mp="2f1m674avmi10" />
                  New menu
                </Button>} data-id="element-1362" data-mp="3cpmd5harg8fk" />

            <ul className="mt-4 space-y-3" data-id="element-1365" data-mp="2oxfluwjw6rn0">
              {menus.map(menu => <Card as="li" key={menu.id} className="overflow-hidden" data-id="element-1366" data-mp="891rkan7h0vn">
                  <div className="flex flex-col gap-4 p-4 sm:flex-row" data-id="element-1367" data-mp="8begj7rdsbi7">
                    <img src={menu.image} alt={menu.name} className="h-28 w-full shrink-0 rounded-lg object-cover sm:w-40" loading="lazy" data-id="element-1368" data-mp="476rf9h0gtjg" />
                    <div className="min-w-0 flex-1" data-id="element-1369" data-mp="1tbcoxc42mp2">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-1370" data-mp="1umh8zddryh8">
                        <h3 className="font-display text-base font-semibold text-ink" data-id="element-1371" data-mp="6t7in24dco79">{menu.name}</h3>
                        {menu.signature && <Badge tone="primary" data-id="element-1372" data-mp="uk365n1skx8p">
                            <SparklesIcon className="h-3 w-3" aria-hidden="true" data-id="element-1373" data-mp="zfs79utgmm74" /> Signature
                          </Badge>}
                        {menu.fulfillment.includes('delivery') && <Badge tone="neutral" data-id="element-1374" data-mp="ppfrsh0i5j7u">
                            <TruckIcon className="h-3 w-3" aria-hidden="true" data-id="element-1375" data-mp="1dguwggfr4cm9" /> Delivers
                          </Badge>}
                      </div>
                      <p className="mt-1 text-[13px] text-ink-2" data-id="element-1376" data-mp="1i95x5libg2fv">{menu.tagline}</p>
                      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-3" data-id="element-1377" data-mp="3qpp5v6hbm2fx">
                        <li className="tnum" data-id="element-1378" data-mp="3lnzrphysx8nm">
                          {currency(menu.pricePerGuest, locale, currencyCode)} / guest
                        </li>
                        <li className="tnum" data-id="element-1379" data-mp="3brwmu4ckevwz">min {menu.minGuests} guests</li>
                        <li className="tnum" data-id="element-1380" data-mp="d97x62n0y432">{menu.includedHours} hrs included</li>
                        <li data-id="element-1381" data-mp="8aeiw7zwy91u">{GROCERY_LABEL[menu.groceries]}</li>
                        <li data-id="element-1382" data-mp="8awmxqsthrkb">{menu.courses.length} courses</li>
                      </ul>
                      {!!menu.equipmentNeeded.length && <p className="mt-2 text-[12px] text-ink-3" data-id="element-1383" data-mp="3uq8ypxuv2jw9">
                          Needs: {menu.equipmentNeeded.map(id => equipmentLabel(id).toLowerCase()).join(', ')}
                        </p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end" data-id="element-1384" data-mp="r7xu9i9ug3rf">
                      <span className="tnum font-display text-lg font-semibold text-ink" data-id="element-1385" data-mp="zjhfe206vyos">
                        {currency(menu.pricePerGuest * menu.minGuests, locale, currencyCode)}
                      </span>
                      <span className="text-[12px] text-ink-3" data-id="element-1386" data-mp="18knymiu83nlq">smallest booking</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(menu)} data-id="element-1387" data-mp="1dj8yui9h67a6">
                        <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-1388" data-mp="1igilxr9e66uu" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </Card>)}
            </ul>
          </section>

          <section aria-labelledby="rules-heading" data-id="element-1389" data-mp="1w8f9di047skw">
            <SectionHeading level={2} title="Booking rules" hint="These apply across every menu and are enforced before a request can reach you." data-id="element-1390" data-mp="1wa9uawbdzkoz" />
            <Card className="mt-4 p-5" data-id="element-1391" data-mp="2648s5j4fgib4">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-1392" data-mp="1ghejwvey0rt7">
                <Field label="Minimum order, in-home" id="m-min" hint="requests below this are blocked" data-id="element-1393" data-mp="1gfv2v2lgqcqd">
                  <Input id="m-min" type="number" min={0} step={100} value={minimumSpend} onChange={e => setMinimumSpend(Number(e.target.value) || 0)} className="tnum" data-id="element-1394" data-mp="1q9kco7d0rd9g" />
                </Field>
                <Field label="Minimum delivery order" id="m-mindel" data-id="element-1395" data-mp="297erx34qql0a">
                  <Input id="m-mindel" type="number" min={0} step={100} value={minDelivery} onChange={e => setMinDelivery(Number(e.target.value) || 0)} className="tnum" data-id="element-1396" data-mp="294irn85f7k8o" />
                </Field>
                <Field label="Deposit" id="m-dep" hint="% of the total, taken on acceptance" data-id="element-1397" data-mp="2li5tow17fbyp">
                  <Select id="m-dep" value={String(deposit)} onChange={e => setDeposit(Number(e.target.value))} data-id="element-1398" data-mp="2qf5xt1ehu4n6">
                    {[20, 25, 30, 40, 50].map(p => <option key={p} value={p} data-id="element-1399" data-mp="2gq4fh4kh7b61">
                        {p}%
                      </option>)}
                  </Select>
                </Field>
                <Field label="Cancellation tier" id="m-cancel" data-id="element-1400" data-mp="26s890q2hh4e9">
                  <Select id="m-cancel" defaultValue={chef.cancellationTier} data-id="element-1401" data-mp="26trpago53fg1">
                    <option value="flexible" data-id="element-1402" data-mp="1stgx9ab3futa">Flexible — full refund to 48 hrs</option>
                    <option value="moderate" data-id="element-1403" data-mp="1xqqkqkex9weh">Moderate — full to 7 days, then 50%</option>
                    <option value="strict" data-id="element-1404" data-mp="3n5yfu4kzm6rl">Strict — deposit non-refundable</option>
                  </Select>
                </Field>
                <Field label="Travel included" id="m-travel" hint="km before charges start" data-id="element-1405" data-mp="3s48cykq9vhfu">
                  <Input id="m-travel" type="number" min={0} value={travelIncluded} onChange={e => setTravelIncluded(Number(e.target.value) || 0)} className="tnum" data-id="element-1406" data-mp="2exesh9vlheyr" />
                </Field>
                <Field label="Per km beyond that" id="m-perkm" data-id="element-1407" data-mp="2157awpcvtukn">
                  <Input id="m-perkm" type="number" min={0} value={travelPerKm} onChange={e => setTravelPerKm(Number(e.target.value) || 0)} className="tnum" data-id="element-1408" data-mp="1w99r292eu634" />
                </Field>
              </div>

              <div className="mt-3 border-t border-hairline" data-id="element-1409" data-mp="1iasacvax9tup">
                <Switch checked={instant} onChange={setInstant} label="Instant book" description="Guests can confirm your published menus without waiting for a quote. You keep the right to decline within 12 hours." data-id="element-1410" data-mp="3hk8l42kx62ok" />
              </div>

              <div className="mt-4 flex items-center gap-3" data-id="element-1411" data-mp="3p2zo46d1u6rz">
                <Button type="button" variant="action" onClick={flash} data-id="element-1412" data-mp="3twu4aztg43mb">
                  Save booking rules
                </Button>
                {saved && <span role="status" className="text-[13px] font-medium text-success" data-id="element-1413" data-mp="3173rcu3jv7rs">
                    Saved
                  </span>}
              </div>
            </Card>
          </section>

          <section aria-labelledby="addon-heading" data-id="element-1414" data-mp="3b4rx0f2p1zoq">
            <SectionHeading level={2} title="Add-ons" hint="Priced separately and shown as their own quote lines." data-id="element-1415" data-mp="3b40090xd1vwv" />
            <Card className="mt-4 divide-y divide-hairline" data-id="element-1416" data-mp="3f4jxe4hxozj0">
              {chef.addOns.map(addOn => <div key={addOn.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-1417" data-mp="ugnr5cbem80y">
                  <span className="min-w-0 flex-1" data-id="element-1418" data-mp="2dpcpxfoxq0qk">
                    <span className="block text-sm font-medium text-ink" data-id="element-1419" data-mp="2dol775gfrxhp">{addOn.name}</span>
                    <span className="block text-[12px] text-ink-3" data-id="element-1420" data-mp="2inea63y3k63m">{addOn.description}</span>
                  </span>
                  <span className="tnum shrink-0 text-sm font-medium text-ink" data-id="element-1421" data-mp="2nfo55ye2jwcr">
                    {currency(addOn.price, locale, currencyCode)}
                    <span className="text-[12px] font-normal text-ink-3" data-id="element-1422" data-mp="1uqqmvv9uxzk0">
                      {addOn.unit === 'per_guest' ? ' / guest' : addOn.unit === 'per_hour' ? ' / hr' : addOn.unit === 'per_km' ? ' / km' : ' flat'}
                    </span>
                  </span>
                </div>)}
              <div className="px-5 py-3.5" data-id="element-1423" data-mp="3g5xtf1z40icc">
                <Button type="button" variant="ghost" size="sm" data-id="element-1424" data-mp="3l2eqwms78923">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1425" data-mp="3l4r82xq4ufe3" />
                  Add an add-on
                </Button>
              </div>
            </Card>
          </section>
        </div>

        <aside className="space-y-4" data-id="element-1426" data-mp="14iksypvrampx">
          <Card className="p-5" data-id="element-1427" data-mp="zp8akxho7c5m">
            <div className="flex items-center gap-2" data-id="element-1428" data-mp="zo6xknh3gfl9">
              <UtensilsIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1429" data-mp="ur6pk70op2ri" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1430" data-mp="prlp1s5v8mco">How you appear in search</h2>
            </div>
            <p className="tnum mt-3 font-display text-2xl font-semibold text-primary" data-id="element-1431" data-mp="loxeng1i77dl">
              from {currency(cheapest, locale, currencyCode)}
            </p>
            <p className="text-[13px] text-ink-2" data-id="element-1432" data-mp="bs2i32osjh1y">per guest</p>
            <dl className="mt-4" data-id="element-1433" data-mp="btnbgozdcty8">
              <KeyValue label="Smallest table you take" value={`${Math.min(...menus.map(m => m.minGuests))} guests`} data-id="element-1434" data-mp="2b4ohxp7f2rrj" />
              <KeyValue label="Minimum order" value={currency(minimumSpend, locale, currencyCode)} data-id="element-1435" data-mp="265k8cxgkys0o" />
              <KeyValue label="Menus listed" value={String(menus.length)} data-id="element-1436" data-mp="4gec03mf3jyu" />
              <KeyValue label="Cuisines" value={chef.cuisineIds.map(cuisineLabel).join(', ')} data-id="element-1437" data-mp="974jbmeelww6" />
            </dl>
          </Card>

          <Card className="p-5" data-id="element-1438" data-mp="3qoxiexzeekia">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1439" data-mp="3qppd86l68e7i">On a typical booking</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-1440" data-mp="3vkmid2fby3f4">
              Six guests on your cheapest menu bills{' '}
              <span className="tnum font-semibold text-ink" data-id="element-1441" data-mp="s6s5lyknhqvj">{currency(cheapest * 6, locale, currencyCode)}</span>, of which
              you keep{' '}
              <span className="tnum font-semibold text-primary" data-id="element-1442" data-mp="db2ifx3qb7p8">
                {currency(Math.round(cheapest * 6 * (1 - SERVICE_FEE_RATE)), locale, currencyCode)}
              </span>{' '}
              after the 10% service fee.
            </p>
          </Card>
        </aside>
      </div>

      {editing && <MenuEditor menu={editing} onClose={() => setEditing(null)} onSave={save} data-id="element-1443" data-mp="32w76gorhw180" />}
    </AppShell>;
}
function MenuEditor({
  menu,
  onClose,
  onSave
}: {
  menu: ChefMenu;
  onClose: () => void;
  onSave: (menu: ChefMenu) => void;
}) {
  const {
    locale,
    currencyCode
  } = useLocale();
  const [draft, setDraft] = useState<ChefMenu>(menu);
  const set = <K extends keyof ChefMenu,>(key: K, value: ChefMenu[K]) => setDraft({
    ...draft,
    [key]: value
  });
  const delivers = draft.fulfillment.includes('delivery');
  return <Sheet open onClose={onClose} title={menu.name} description="Changes go live immediately" side="right" width="max-w-lg" data-id="element-1444" data-mp="xbci8ux9bkku">
      <div className="space-y-5" data-id="element-1445" data-mp="1b2r55cuac5x6">
        <Field label="Menu name" id="me-name" data-id="element-1446" data-mp="1b3sfff3h8odu">
          <Input id="me-name" value={draft.name} onChange={e => set('name', e.target.value)} data-id="element-1447" data-mp="16b91ui4sht91" />
        </Field>
        <Field label="One-line description" id="me-tag" data-id="element-1448" data-mp="ileo7ycnc0nq">
          <Input id="me-tag" value={draft.tagline} onChange={e => set('tagline', e.target.value)} data-id="element-1449" data-mp="ijtw0fdt0tt1" />
        </Field>

        <div className="grid grid-cols-2 gap-3" data-id="element-1450" data-mp="ra3w76q5jv2l">
          <Field label="Price per guest" id="me-price" data-id="element-1451" data-mp="rbezz6jll2tp">
            <Input id="me-price" type="number" min={0} step={50} value={draft.pricePerGuest} onChange={e => set('pricePerGuest', Number(e.target.value) || 0)} className="tnum" data-id="element-1452" data-mp="w8dpfbsbgllk" />
          </Field>
          <Field label="Minimum guests" id="me-min" data-id="element-1453" data-mp="1aykyx20ciua4">
            <Input id="me-min" type="number" min={1} value={draft.minGuests} onChange={e => set('minGuests', Number(e.target.value) || 1)} className="tnum" data-id="element-1454" data-mp="1azw1wzlyvy06" />
          </Field>
          <Field label="Hours included" id="me-hours" data-id="element-1455" data-mp="1ox2iqon4rmmr">
            <Input id="me-hours" type="number" min={0} step={0.5} value={draft.includedHours} onChange={e => set('includedHours', Number(e.target.value) || 0)} className="tnum" data-id="element-1456" data-mp="3lq4cmzn2hdz0" />
          </Field>
          <Field label="Lead time" id="me-lead" hint="days of notice" data-id="element-1457" data-mp="3ee6fe9nwx3om">
            <Input id="me-lead" type="number" min={0} value={draft.leadTimeDays} onChange={e => set('leadTimeDays', Number(e.target.value) || 0)} className="tnum" data-id="element-1458" data-mp="3ecvfikxl8g2s" />
          </Field>
        </div>

        <Field label="Groceries" id="me-groc" data-id="element-1459" data-mp="5wk1l64uykc8">
          <Select id="me-groc" value={draft.groceries} onChange={e => set('groceries', e.target.value as GroceryPolicy)} data-id="element-1460" data-mp="10uzow5d8rcr">
            <option value="included" data-id="element-1461" data-mp="3n79zv1wz6gsr">Included in the price</option>
            <option value="at_cost" data-id="element-1462" data-mp="3n94annej8sj4">At cost, with receipts</option>
            <option value="estimate" data-id="element-1463" data-mp="2grqzhcb4we36">Estimated, trued up to receipts</option>
          </Select>
        </Field>

        <div className="border-y border-hairline" data-id="element-1464" data-mp="2bqbefk8yi7ks">
          <Switch checked={delivers} onChange={on => set('fulfillment', on ? ['in_home', 'delivery'] : ['in_home'])} label="Offer this as delivery" description="Cooked in your kitchen, delivered cold or hot. Priced per portion." data-id="element-1465" data-mp="13sp5h3o4m3r6" />
          <Switch checked={draft.signature} onChange={on => set('signature', on)} label="Signature menu" description="Pinned to the top of your profile and used in search previews." data-id="element-1466" data-mp="ytckkogtf6r8" />
        </div>

        {delivers && <Field label="Price per portion" id="me-portion" hint="what one person's delivered portion costs" data-id="element-1467" data-mp="owzqvql9sswh">
            <Input id="me-portion" type="number" min={0} step={10} value={draft.pricePerPortion ?? 0} onChange={e => set('pricePerPortion', Number(e.target.value) || 0)} className="tnum" data-id="element-1468" data-mp="g2uteobmsj7z" />
          </Field>}

        <div data-id="element-1469" data-mp="3inhqzdckjzc9">
          <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-1470" data-mp="3ipb152me51p0">Courses</p>
          <ol className="space-y-2" data-id="element-1471" data-mp="3en64rw4k5qyo">
            {draft.courses.map((course, i) => <li key={`${course.name}-${i}`} className="rounded-lg border border-hairline p-3" data-id="element-1472" data-mp="3sm5xpfz3ss0n" data-mpi={i}>
                <p className="text-sm font-medium text-ink" data-id="element-1473" data-mp="3nnm6p15nkq1f">{course.name}</p>
                <p className="mt-0.5 text-[12px] text-ink-3" data-id="element-1474" data-mp="3npp96knzsbn1">{course.description}</p>
              </li>)}
          </ol>
          <Button type="button" variant="ghost" size="sm" className="mt-2" data-id="element-1475" data-mp="1go12e5ky4oa">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1476" data-mp="f7jbhuvq8ttz" />
            Add a course
          </Button>
        </div>

        <div className="rounded-xl bg-oat p-4" data-id="element-1477" data-mp="a7y1mpf0r12h">
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1478" data-mp="a9819j1ishb8">Smallest booking this allows</p>
          <p className="tnum mt-1 font-display text-xl font-semibold text-ink" data-id="element-1479" data-mp="p3ciyzul9x4w">
            {currency(draft.pricePerGuest * draft.minGuests, locale, currencyCode)}
          </p>
          <p className="mt-0.5 text-[12px] text-ink-3" data-id="element-1480" data-mp="30zfqwibv4mh0">
            {draft.minGuests} guests × {currency(draft.pricePerGuest, locale, currencyCode)}
          </p>
        </div>

        <div className="flex gap-2" data-id="element-1481" data-mp="3az6lsl4c53hm">
          <Button type="button" variant="action" full onClick={() => onSave(draft)} data-id="element-1482" data-mp="3aw0wb4rsnzld">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1483" data-mp="3fw6p4o9svs51" />
            Save menu
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-1484" data-mp="2i73et3veq6to">
            Cancel
          </Button>
        </div>
      </div>
    </Sheet>;
}